import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../../shared/ui";
import { useRoles } from "../../../../shared/api/hooks/useRoles";
import { useAdmins } from "../../../../shared/api/hooks/useAdmins";
import { IssueReason } from "../../types";

interface FlagTransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    issueReason: IssueReason | string;
    escalateType: "role" | "member";
    escalateTargetName: string;
    roleId?: string;
    adminId?: string;
    note: string;
  }) => void;
}

const ISSUE_OPTIONS: IssueReason[] = [
  "Transaction failed but was debited",
  "Transaction successful but recipient hasn't received funds",
  "Suspicious activity",
  "Other",
];

export const FlagTransactionModal: React.FC<FlagTransactionModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
}) => {
  const [selectedIssue, setSelectedIssue] = useState<IssueReason | "">("");
  const [escalateType, setEscalateType] = useState<"role" | "member">("role");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedRoleName, setSelectedRoleName] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [note, setNote] = useState("");

  const { roles, isLoading: isLoadingRoles } = useRoles();
  const { admins, isLoading: isLoadingAdmins } = useAdmins({
    isProfileCompleted: true,
  });

  const roleList = useMemo(() => {
    if (roles && roles.length > 0) {
      return roles
        .map((r: any) => ({
          id: String(r._id || r.id || ""),
          name: r.name || "",
        }))
        .filter((r) => Boolean(r.id));
    }
    return [];
  }, [roles]);

  const memberList = useMemo(() => {
    if (admins && admins.length > 0) {
      return admins
        .map((a: any) => {
          const adminId = String(a._id || a.id || "");
          const roleId =
            (typeof a.role === "string" ? a.role : a.role?._id) ||
            a.roles?.[0]?._id ||
            adminId;
          return {
            id: adminId,
            roleId: String(roleId),
            name:
              `${a.firstName || ""} ${a.lastName || ""}`.trim() ||
              a.email ||
              "Unnamed member",
            email: a.email,
          };
        })
        .filter((m) => Boolean(m.id));
    }
    return [];
  }, [admins]);

  useEffect(() => {
    if (roleList.length > 0 && !selectedRoleId) {
      setSelectedRoleId(roleList[0].id);
      setSelectedRoleName(roleList[0].name);
    }
  }, [roleList, selectedRoleId]);

  useEffect(() => {
    if (memberList.length > 0 && !selectedMemberId) {
      setSelectedMemberId(memberList[0].id);
      setSelectedMemberName(memberList[0].name);
    }
  }, [memberList, selectedMemberId]);

  const handleClose = () => {
    onOpenChange(false);
    setSelectedIssue("");
    setSelectedRoleId("");
    setSelectedRoleName("");
    setSelectedMemberId("");
    setSelectedMemberName("");
    setNote("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIssue) return;

    const targetName =
      escalateType === "role" ? selectedRoleName : selectedMemberName;

    const selectedMember = memberList.find((m) => m.id === selectedMemberId);
    const resolvedRoleId =
      escalateType === "role"
        ? selectedRoleId
        : (selectedMember?.roleId || selectedMemberId);

    onSubmit({
      issueReason: selectedIssue,
      escalateType,
      escalateTargetName: targetName,
      roleId: resolvedRoleId,
      adminId: escalateType === "member" ? selectedMemberId : undefined,
      note,
    });
    handleClose();
  };

  const hasTargetSelected =
    escalateType === "role" ? Boolean(selectedRoleId) : Boolean(selectedMemberId);
  const canSubmit = Boolean(selectedIssue && hasTargetSelected);

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[520px] bg-white rounded-[24px]"
    >
      <ModalHeader className="items-center justify-between pb-3 border-b border-[#EBEDF9]">
        <ModalTitle className="text-[20px] font-[600] text-[#161827]">
          Flag transaction
        </ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          onClick={handleClose}
          className="rounded-full p-2 text-[#161827] hover:bg-[#F2F4FF] transition-colors"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <form onSubmit={handleSubmit}>
        <ModalBody className="flex flex-col gap-6 py-6">
          {/* Field 1: What's the issue? */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              What&apos;s the issue?
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-4 text-left transition-colors hover:bg-[#E2E6F5] focus:outline-none"
                >
                  <span
                    className={`text-[14px] font-[400] truncate ${
                      selectedIssue ? "text-[#161827]" : "text-[#737AA8]"
                    }`}
                  >
                    {selectedIssue || "Select transaction issue"}
                  </span>
                  <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="z-[125] max-h-[240px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                >
                  {ISSUE_OPTIONS.map((opt) => {
                    const isSelected = selectedIssue === opt;
                    return (
                      <DropdownMenu.Item
                        key={opt}
                        onSelect={() => setSelectedIssue(opt)}
                        className={`flex items-center cursor-pointer rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${
                          isSelected
                            ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                            : "text-[#383B56]"
                        }`}
                      >
                        <span className="truncate">{opt}</span>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Field 2: Escalate to... Radio */}
          <div className="flex flex-col gap-2.5">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Escalate to...
            </p>
            <div className="flex items-center gap-8">
              {(["role", "member"] as const).map((type) => {
                const isActive = escalateType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setEscalateType(type)}
                    className="inline-flex items-center gap-3 text-[14px] font-[450] text-[#2C2F44] transition-colors focus:outline-none"
                  >
                    <span
                      className={`grid h-5 w-5 place-items-center rounded-full border transition-colors ${
                        isActive ? "border-[#6F00FF]" : "border-[#C7CEE4]"
                      }`}
                    >
                      {isActive ? (
                        <span className="h-2.5 w-2.5 rounded-full bg-[#6F00FF]" />
                      ) : null}
                    </span>
                    <span className="capitalize">{type}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Field 3: Role / Member Selection */}
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44] capitalize">
              {escalateType}
            </p>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex h-[48px] w-full items-center justify-between rounded-[8px] bg-[#E9ECF8] px-4 text-left transition-colors hover:bg-[#E2E6F5] focus:outline-none"
                >
                  <span
                    className={`text-[14px] font-[400] truncate ${
                      (escalateType === "role" ? selectedRoleName : selectedMemberName)
                        ? "text-[#161827]"
                        : "text-[#737AA8]"
                    }`}
                  >
                    {escalateType === "role"
                      ? selectedRoleName || (isLoadingRoles ? "Loading roles..." : "Select role")
                      : selectedMemberName || (isLoadingAdmins ? "Loading members..." : "Select member")}
                  </span>
                  <ChevronDown size={18} className="text-[#6F739B] shrink-0" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  align="start"
                  sideOffset={6}
                  className="z-[125] max-h-[240px] w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto rounded-[12px] border border-[#E1E6FF] bg-white p-1.5 shadow-lg"
                >
                  {escalateType === "role" ? (
                    roleList.length ? (
                      roleList.map((r) => {
                        const isSelected = r.name === selectedRoleName;
                        return (
                          <DropdownMenu.Item
                            key={r.id}
                            onSelect={() => {
                              setSelectedRoleId(r.id);
                              setSelectedRoleName(r.name);
                            }}
                            className={`flex items-center cursor-pointer rounded-[8px] px-3.5 py-2.5 text-[14px] outline-none transition-colors focus:bg-[#F5F6FD] ${
                              isSelected
                                ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                                : "text-[#383B56]"
                            }`}
                          >
                            <span className="truncate">{r.name}</span>
                          </DropdownMenu.Item>
                        );
                      })
                    ) : (
                      <div className="px-3.5 py-2 text-[13px] text-[#6F739B]">
                        {isLoadingRoles ? "Loading roles..." : "No roles found."}
                      </div>
                    )
                  ) : memberList.length ? (
                    memberList.map((m) => {
                      const isSelected = m.name === selectedMemberName;
                      return (
                        <DropdownMenu.Item
                          key={m.id}
                          onSelect={() => {
                            setSelectedMemberId(m.id);
                            setSelectedMemberName(m.name);
                          }}
                          className={`cursor-pointer rounded-[8px] px-3.5 py-2 outline-none transition-colors focus:bg-[#F5F6FD] ${
                            isSelected
                              ? "bg-[#F1ECFF] text-[#5E01D6] font-[500]"
                              : "text-[#383B56]"
                          }`}
                        >
                          <div className="text-[14px] font-[500] text-[#161827]">
                            {m.name}
                          </div>
                          {m.email && (
                            <div className="text-[12px] text-[#6F739B]">
                              {m.email}
                            </div>
                          )}
                        </DropdownMenu.Item>
                      );
                    })
                  ) : (
                    <div className="px-3.5 py-2 text-[13px] text-[#6F739B]">
                      {isLoadingAdmins ? "Loading members..." : "No members found."}
                    </div>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {/* Field 4: Note • Optional (Commented out per backend requirement: property note should not exist) */}
          {/*
          <div className="flex flex-col gap-2">
            <p className="text-[14px] font-[450] text-[#2C2F44]">
              Note <span className="font-normal italic text-[#6F739B]">• Optional</span>
            </p>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add notes"
              className="min-h-[100px] w-full resize-none rounded-[8px] bg-[#E9ECF8] p-4 text-[14px] text-[#161827] placeholder:text-[#9A9CC0] focus:outline-none focus:ring-2 focus:ring-[#D9DEFF]"
            />
          </div>
          */}
        </ModalBody>

        <ModalFooter className="flex items-center justify-end border-t border-[#EBEDF9] px-6 py-4">
          <Button
            type="submit"
            disabled={!canSubmit}
            className="h-10 rounded-[8px] bg-[#020618] px-6 text-[14px] font-medium text-white shadow-xs transition-colors hover:bg-[#0f172a] disabled:bg-[#90A1B9] disabled:text-white disabled:cursor-not-allowed"
          >
            Submit issue
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
